import { useEffect, useState, useCallback, useRef } from "react";
import {
  HiOutlineAcademicCap,
  HiOutlineChartPie,
  HiOutlineClipboardCheck,
  HiOutlineDocumentReport,
  HiOutlineCheckCircle,
  HiOutlineExclamationCircle,
  HiOutlineClock,
  HiOutlineX,
  HiOutlineOfficeBuilding,
} from "react-icons/hi";
import { fetchDashboard, fetchCompetencyStats, fetchFIComplianceReport, exportAssessmentData } from "../services/dashboardApi";
import Pagination from "../components/Pagination";
import { fetchMasterData, fetchSchools, fetchCycles } from "../services/masterDataApi";
import { useUserRole } from "../hooks/useUserRole";
import { fetchMyStates, fetchMyBlocks, fetchMyDistricts } from "../services/assignmentApi";
import SearchableDropdown from "../components/SearchableDropdown";
import ChipMultiSelect from "../components/ChipMultiSelect";

// Comparison operator symbols for student-category thresholds
const OP_SYMBOL = { gte: "≥", gt: ">", lte: "≤", lt: "<", eq: "=" };

const selectCls = "h-[34px] border border-[#d0d5dd] rounded-lg px-2.5 text-[12.5px] bg-white focus:outline-none focus:border-[#0078d4] focus:ring-1 focus:ring-[#0078d4]/20 transition-all appearance-none";

// Hoisted to module scope (not defined inside Dashboard) so its component
// identity stays stable across renders — otherwise React remounts this
// subtree (and wipes SearchableDropdown's internal open/label state) on
// every Dashboard re-render, including the one triggered by picking a value.
const FilterPanel = ({ fState, onChange, opts, showGrade, onClear, hasClear, role, isPmOrPo, splStates, pmBlocks, plDistricts, adminStates, pmDistrictOpts, children }) => (
    <div className="bg-white rounded-xl border border-[#e5e7eb] mb-5">
      <div className="px-5 py-3 border-b border-[#f0f0f0]">
        <span className="text-[11px] font-semibold text-[#8b949e] uppercase tracking-wider">Filters</span>
      </div>
      <div className="px-5 py-4 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">State</label>
            {role === "PM" ? (
              <select value={fState.state_id || ""} onChange={e => onChange("state_id", e.target.value)} className={selectCls + " w-full"}>
                {[...new Map(pmBlocks.map(b => [b.state_id, { id: b.state_id, name: b.state_name }])).values()]
                  .map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            ) : role === "SPL" ? (
              <select value={fState.state_id || ""} onChange={e => onChange("state_id", e.target.value)} className={selectCls + " w-full"}>
                {splStates.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            ) : role === "PL" ? (
              <select value={fState.state_id || ""} onChange={e => onChange("state_id", e.target.value)} className={selectCls + " w-full"}>
                {[...new Map(plDistricts.map(d => [d.state_id, { id: d.state_id, name: d.state_name }])).values()]
                  .map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            ) : isPmOrPo ? (
              <div className={selectCls + " w-full bg-[#f6f8fa] text-[#57606a] flex items-center"}>
                {adminStates.find(s => String(s.id) === String(fState.state_id))?.name || (fState.state_id ? `State #${fState.state_id}` : "—")}
              </div>
            ) : (
              <SearchableDropdown
                value={String(fState.state_id || "")}
                onChange={(id) => onChange("state_id", id)}
                placeholder="Select State"
                fetchFn={(search, page = 1) => fetchMasterData("states", {
                  page, page_size: 20, search,
                  has_assessment: true,
                  ...(fState.cycle_type ? { cycle_type: fState.cycle_type.toLowerCase() } : {}),
                })}
                variant="report"
              />
            )}
          </div>
          <div>
            <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">Cycle Type</label>
            <select value={fState.cycle_type || ""} onChange={e => onChange("cycle_type", e.target.value)} className={selectCls + " w-full"}>
              <option value="">Select a Cycle Type</option>
              <option value="Continuous">Continuous</option>
              <option value="Quarterly">Quarterly</option>
            </select>
          </div>
          <div>
            <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">Cycle</label>
            <select value={fState.cycle_id || ""} onChange={e => onChange("cycle_id", e.target.value)} className={selectCls + " w-full"}>
              <option value="">Select a Cycle</option>
              {opts.cycles.map(c => <option key={c.id} value={c.id}>{c.title || (c.cycle_type && c.round_number !== undefined ? `${c.cycle_type} R${c.round_number}` : `Cycle ${c.id}`)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">District</label>
            {role === "PM" ? (
              <select value={fState.district_id || ""} onChange={e => onChange("district_id", e.target.value)} className={selectCls + " w-full"}>
                <option value="">All Districts</option>
                {pmDistrictOpts.slice().sort((a, b) => a.name.localeCompare(b.name)).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            ) : role === "PL" ? (
              <select value={fState.district_id || ""} onChange={e => onChange("district_id", e.target.value)} className={selectCls + " w-full"}>
                <option value="">All Districts</option>
                {plDistricts.slice().sort((a, b) => a.name.localeCompare(b.name)).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            ) : (
              <SearchableDropdown
                key={`dash-dist-${fState.state_id}`}
                value={String(fState.district_id || "")}
                onChange={(id) => onChange("district_id", id)}
                placeholder="All Districts"
                fetchFn={(search, page = 1) => fetchMasterData("districts", {
                  page, page_size: 20, state_id: Number(fState.state_id), search,
                  has_assessment: true,
                  ...(fState.cycle_id ? { cycle_id: fState.cycle_id } : {}),
                  ...(fState.cycle_type ? { cycle_type: fState.cycle_type.toLowerCase() } : {}),
                })}
                disabled={!fState.state_id}
                variant="report"
              />
            )}
          </div>
          <div>
            <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">Block</label>
            {role === "PM" ? (
              <select value={fState.block_id || ""} onChange={e => onChange("block_id", e.target.value)} className={selectCls + " w-full"}>
                <option value="">All Blocks</option>
                {pmBlocks.filter(b => !fState.district_id || String(b.district_id) === String(fState.district_id)).slice().sort((a, b) => a.name.localeCompare(b.name)).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            ) : (
              <SearchableDropdown
                key={`dash-blk-${fState.district_id}-${fState.state_id}`}
                value={String(fState.block_id || "")}
                onChange={(id) => onChange("block_id", id)}
                placeholder="All Blocks"
                fetchFn={(search, page = 1) => fetchMasterData("blocks", {
                  page, page_size: 20,
                  ...(fState.district_id ? { district_id: Number(fState.district_id) } : { state_id: Number(fState.state_id) }),
                  search,
                  has_assessment: true,
                  ...(fState.cycle_id ? { cycle_id: fState.cycle_id } : {}),
                  ...(fState.cycle_type ? { cycle_type: fState.cycle_type.toLowerCase() } : {}),
                })}
                disabled={!fState.state_id}
                variant="report"
              />
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[160px]">
            <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">School</label>
            <SearchableDropdown
              key={`dash-sch-${fState.block_id}-${fState.district_id}-${fState.state_id}`}
              value={String(fState.school_id || "")}
              onChange={(id) => onChange("school_id", id)}
              placeholder="All Schools"
              fetchFn={(search, page = 1) => fetchSchools({
                page, page_size: 20,
                ...(fState.block_id ? { block_id: Number(fState.block_id) } : fState.district_id ? { district_id: Number(fState.district_id) } : { state_id: Number(fState.state_id) }),
                // Quarterly cycles only hold non-adopted schools, so restrict to adopted ones for continuous only.
                ...(fState.cycle_type?.toLowerCase() === "continuous" ? { is_adopted: true } : {}),
                search,
                has_assessment: true,
                ...(fState.cycle_id ? { cycle_id: fState.cycle_id } : {}),
                ...(fState.cycle_type ? { cycle_type: fState.cycle_type.toLowerCase() } : {}),
              })}
              disabled={!fState.state_id}
              variant="report"
            />
          </div>
          {showGrade && (
            <div className="min-w-[110px]">
              <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">Grade</label>
              <select value={fState.grade || ""} onChange={e => onChange("grade", e.target.value)} className={selectCls + " w-full"}>
                <option value="">All Grades</option>
                {[1,2,3,4,5,6,7,8].map(g => <option key={g} value={g}>Grade {g}</option>)}
              </select>
            </div>
          )}
          {hasClear && (
            <button onClick={onClear} className="inline-flex items-center gap-1 h-[34px] px-3 text-[12px] font-medium rounded-lg cursor-pointer bg-[#f3f4f6] text-[#57606a] border border-[#d0d5dd] hover:bg-[#e5e7eb] transition-all">
              <HiOutlineX className="text-xs" /> Clear All
            </button>
          )}
        </div>
        {children && <div className="pt-3 border-t border-[#f0f0f0]">{children}</div>}
      </div>
    </div>
);

// Hoisted to module scope for the same reason as FilterPanel above.
const CategoryCard = ({ cat, type, total }) => {
    if (!cat) return null;
    const label = type === "student" ? "Students" : "Schools";
    return (
      <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden h-full">
        <div className="p-4 h-full flex flex-col">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: cat.color }}>
              {cat.name.charAt(0)}
            </div>
            <div>
              <div className="text-[13px] font-semibold text-[#24292f]">Category {cat.name}</div>
              <div className="text-[10px] text-[#8b949e]">
                {type === "student" ? `Threshold: ${OP_SYMBOL[cat.operator || "gte"]} ${cat.threshold}%` : cat.label || ""}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-2 flex-1">
            <div className="bg-[#f8f9fa] rounded-lg px-3 py-2 text-center flex flex-col justify-center">
              <div className="text-[18px] font-bold text-[#24292f]">{(cat.count || 0).toLocaleString()}</div>
              <div className="text-[9px] text-[#8b949e] font-medium uppercase">{label}</div>
            </div>
            <div className="bg-[#f8f9fa] rounded-lg px-3 py-2 text-center flex flex-col justify-center">
              <div className="text-[18px] font-bold" style={{ color: cat.color }}>{cat.percentage || 0}%</div>
              <div className="text-[9px] text-[#8b949e] font-medium uppercase">of Total</div>
            </div>
          </div>
          <div className="text-[10px] text-[#8b949e] text-center mt-auto">
            Total: {(total || 0).toLocaleString()} {label.toLowerCase()}
          </div>
        </div>
      </div>
    );
};

function Dashboard() {
  const { role, userProfile } = useUserRole();
  const [activeTab, setActiveTab] = useState("categorisation");
  const [splStates, setSplStates] = useState([]);
  const [adminStates] = useState([]);
  const [pmBlocks, setPmBlocks] = useState([]);
  const [pmDistrictOpts, setPmDistrictOpts] = useState([]);
  const [plDistricts, setPlDistricts] = useState([]);
  const [splStateLoaded, setSplStateLoaded] = useState(false);

  /* ── Categorisation ── */
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ cycle_id: "", state_id: "", district_id: "", block_id: "", school_id: "", grade: "", cycle_type: "" });
  const [filterOptions, setFilterOptions] = useState({ cycles: [], states: [], districts: [], blocks: [], schools: [] });

  /* ── PO Compliance ── */
  const [fiData, setFiData] = useState(null);
  const [fiLoading, setFiLoading] = useState(false);
  const [fiFilters, setFiFilters] = useState({ cycle_id: "", state_id: "", district_id: "", block_id: "", school_id: "", cycle_type: "" });
  const [fiFilterOptions, setFiFilterOptions] = useState({ cycles: [], states: [], districts: [], blocks: [], schools: [] });
  const [exportCycleIds, setExportCycleIds] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  // A completed round can hold ~1000 visits; the endpoint has always paginated
  // (default page_size 50) but this table never asked for a page, so everything
  // past the first 50 was unreachable.
  const FI_PAGE_SIZE = 50;
  const [fiPage, setFiPage] = useState(1);

  /* ── Competency ── */
  const [compData, setCompData] = useState(null);
  const [compLoading, setCompLoading] = useState(false);
  const [compFilters, setCompFilters] = useState({ cycle_id: "", grade: "", state_id: "", cycle_type: "", district_id: "", block_id: "" });
  const [compCycles, setCompCycles] = useState([]);

  /* ── Role-based state pre-selection (SPL / PL / PM / ADMIN) ── */
  useEffect(() => {
    if (role === "SPL") {
      fetchMyStates()
        .then((myStates) => {
          if (myStates && myStates.length > 0) {
            setSplStates(myStates);
            const stateId = String(myStates[0].state_id || myStates[0].id);
            setFilters((prev) => ({ ...prev, state_id: stateId }));
            setFiFilters((prev) => ({ ...prev, state_id: stateId }));
            setCompFilters((prev) => ({ ...prev, state_id: stateId }));
          }
          setSplStateLoaded(true);
        })
        .catch(() => setSplStateLoaded(true));
    } else if (role === "PM") {
      fetchMyBlocks()
        .then((myBlocks) => {
          if (myBlocks && myBlocks.length > 0) {
            setPmBlocks(myBlocks);
            const districtMap = {};
            myBlocks.forEach((b) => {
              if (b.district_id) districtMap[b.district_id] = { id: b.district_id, name: b.district_name };
            });
            setPmDistrictOpts(Object.values(districtMap));
            const stateId = String(myBlocks[0].state_id);
            setFilters((prev) => ({ ...prev, state_id: stateId }));
            setFiFilters((prev) => ({ ...prev, state_id: stateId }));
            setCompFilters((prev) => ({ ...prev, state_id: stateId }));
          }
          setSplStateLoaded(true);
        })
        .catch(() => setSplStateLoaded(true));
    } else if (role === "PL") {
      fetchMyDistricts()
        .then((myDistricts) => {
          if (myDistricts && myDistricts.length > 0) {
            setPlDistricts(myDistricts);
            const stateId = String(myDistricts[0].state_id);
            setFilters((prev) => ({ ...prev, state_id: stateId }));
            setFiFilters((prev) => ({ ...prev, state_id: stateId }));
            setCompFilters((prev) => ({ ...prev, state_id: stateId }));
          }
          setSplStateLoaded(true);
        })
        .catch(() => setSplStateLoaded(true));
    } else if (role === "ADMIN") {
      setSplStateLoaded(true);
    }
  }, [role]);

  /* ── Role-based state pre-selection (PO only — needs userProfile.state_id) ── */
  useEffect(() => {
    if (role !== "PO" || userProfile === null) return;
    if (userProfile?.state_id) {
      const stateId = String(userProfile.state_id);
      setFilters((prev) => ({ ...prev, state_id: stateId }));
      setFiFilters((prev) => ({ ...prev, state_id: stateId }));
      setCompFilters((prev) => ({ ...prev, state_id: stateId }));
    }
    setSplStateLoaded(true);
  }, [role, userProfile]);

  // (No ADMIN states fetch: ADMIN's State picker is a SearchableDropdown with
  // its own fetch, and `adminStates` is only read in the non-ADMIN branches.)

  /* ── Loaders ── */
  const loadDashboard = useCallback(async () => {
    if (!filters.state_id) { setData(null); setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetchDashboard(filters);
      if (res.success) {
        if (res.data.filter_options) setFilterOptions(res.data.filter_options);
        if (filters.cycle_id) setData(res.data);
        else setData(null);
      }
    } catch (err) { console.error("Dashboard load failed:", err); }
    finally { setLoading(false); }
  }, [filters]);

  const loadFI = useCallback(async () => {
    if (!fiFilters.state_id) { setFiData(null); setFiLoading(false); return; }
    setFiLoading(true);
    try {
      const res = await fetchFIComplianceReport({ ...fiFilters, page: fiPage, page_size: FI_PAGE_SIZE });
      if (res.success) {
        if (res.data.filter_options) setFiFilterOptions(res.data.filter_options);
        if (fiFilters.cycle_id) {
          setFiData(res.data);
          // Narrowing a filter can leave us past the last page, which would show
          // an empty table with a populated count. Step back and refetch.
          // Floor at 1: an empty result set reports total_pages 0, and the
          // endpoint rejects page < 1 with a 422.
          const totalPages = Math.max(1, res.data.pagination?.total_pages || 1);
          if (fiPage > totalPages) setFiPage(totalPages);
        } else setFiData(null);
      }
    } catch (err) { console.error("PO report failed:", err); }
    finally { setFiLoading(false); }
  }, [fiFilters, fiPage]);

  const loadComp = useCallback(async () => {
    if (!compFilters.state_id) { setCompData(null); setCompLoading(false); return; }
    setCompLoading(true);
    try {
      const res = await fetchCompetencyStats(compFilters);
      if (res.success) {
        if (compFilters.cycle_id) setCompData(res.data);
        else setCompData(null);
      }
    } catch (err) { console.error("Comp stats failed:", err); }
    finally { setCompLoading(false); }
  }, [compFilters]);

  useEffect(() => { if (splStateLoaded) loadDashboard(); }, [loadDashboard, splStateLoaded]);
  useEffect(() => { if (activeTab === "fi" && splStateLoaded) loadFI(); }, [activeTab, loadFI, splStateLoaded]);
  useEffect(() => { if (activeTab === "competency" && splStateLoaded) loadComp(); }, [activeTab, loadComp, splStateLoaded]);

  // Competency Stats' own Cycle dropdown — fetched independently of the Categorisation
  // tab's filterOptions.cycles, which is gated to completed/scored cycles only and would
  // hide published-but-not-yet-scored cycles.
  // Only fetched while the Competency tab is open, and not again on a tab
  // switch when its state/cycle-type haven't changed.
  const compCyclesKeyRef = useRef(null);
  useEffect(() => {
    if (!compFilters.state_id) { compCyclesKeyRef.current = null; setCompCycles([]); return; }
    if (activeTab !== "competency") return;
    const key = `${compFilters.state_id}|${compFilters.cycle_type || ""}`;
    if (compCyclesKeyRef.current === key) return;
    compCyclesKeyRef.current = key;
    let cancelled = false;
    let done = false;
    fetchCycles({ state_id: compFilters.state_id, cycle_type: compFilters.cycle_type ? compFilters.cycle_type.toLowerCase() : undefined, page_size: 100 })
      .then((res) => { if (!cancelled && res.success) setCompCycles(res.data || []); })
      .catch(() => { if (!cancelled) setCompCycles([]); })
      .finally(() => { done = true; });
    // An interrupted fetch must be retried next time.
    return () => { cancelled = true; if (!done) compCyclesKeyRef.current = null; };
  }, [activeTab, compFilters.state_id, compFilters.cycle_type]);

/* ── Cascading handlers ── */
  const handleFilterChange = (key, value) => {
    const next = { ...filters, [key]: value };
    if (key === "state_id") { next.cycle_id = ""; next.cycle_type = ""; next.district_id = ""; next.block_id = ""; next.school_id = ""; next.grade = ""; }
    if (key === "cycle_type") { next.cycle_id = ""; }
    if (key === "district_id") { next.block_id = ""; next.school_id = ""; }
    if (key === "block_id") { next.school_id = ""; }
    setFilters(next);
  };
  const handleFiFilterChange = (key, value) => {
    const next = { ...fiFilters, [key]: value };
    if (key === "state_id") { next.cycle_id = ""; next.cycle_type = ""; next.district_id = ""; next.block_id = ""; next.school_id = ""; }
    if (key === "cycle_type") { next.cycle_id = ""; }
    if (key === "district_id") { next.block_id = ""; next.school_id = ""; }
    if (key === "block_id") { next.school_id = ""; }
    setFiPage(1);
    setFiFilters(next);
  };

  const handleExportAssessmentData = async () => {
    setExportLoading(true);
    try {
      const blob = await exportAssessmentData({
        cycle_ids: exportCycleIds,
        state_id: fiFilters.state_id,
        district_id: fiFilters.district_id,
        block_id: fiFilters.block_id,
        school_id: fiFilters.school_id,
        cycle_type: fiFilters.cycle_type,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "assessment_data_export.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Assessment data export failed:", err);
    } finally {
      setExportLoading(false);
    }
  };

  /* ── Cascading derived lists ── */
  const derive = (fo, stateId, districtId, blockId, cycleType) => {
    let cycles = stateId ? (fo.cycles || []).filter(c => c.state_id === Number(stateId)) : (fo.cycles || []);
    if (cycleType) cycles = cycles.filter(c => (c.cycle_type || "").toLowerCase() === cycleType.toLowerCase());
    const districts = stateId ? (fo.districts || []).filter(d => d.state_id === Number(stateId)) : (fo.districts || []);
    const blocks = districtId
      ? (fo.blocks || []).filter(b => b.district_id === Number(districtId))
      : stateId
        ? (fo.blocks || []).filter(b => { const d = (fo.districts || []).find(x => x.id === b.district_id); return d?.state_id === Number(stateId); })
        : (fo.blocks || []);
    const schools = blockId
      ? (fo.schools || []).filter(s => s.block_id === Number(blockId))
      : districtId
        ? (fo.schools || []).filter(s => { const b = (fo.blocks || []).find(x => x.id === s.block_id); return b?.district_id === Number(districtId); })
        : (fo.schools || []);
    const sortByName = (a, b) => (a.name || '').localeCompare(b.name || '');
    return { cycles, districts: districts.sort(sortByName), blocks: blocks.sort(sortByName), schools: schools.sort(sortByName), states: (fo.states || []).sort(sortByName) };
  };

  const catOpts = derive(filterOptions, filters.state_id, filters.district_id, filters.block_id, filters.cycle_type);
  const fiOpts = derive(fiFilterOptions, fiFilters.state_id, fiFilters.district_id, fiFilters.block_id, fiFilters.cycle_type);

  /* ── Derived data ── */
  const categories = data?.categories || [];
  const schoolCategories = data?.school_categories || [];
  const cycleName = (c) => c.title || (c.cycle_type && c.round_number !== undefined ? `${c.cycle_type} R${c.round_number}` : `Cycle ${c.id}`);

  /* ── Styles ── */
  const tabCls = (t) => `px-4 py-2 text-[12.5px] font-medium rounded-md transition-all cursor-pointer border-none ${activeTab === t ? "bg-white text-[#0078d4] shadow-sm font-semibold" : "text-[#57606a] hover:text-[#24292f] bg-transparent"}`;
  const hasAnyFilter = (f) => Object.values(f).some(v => v !== "");

  const isPmOrPo = role === "SPL" || role === "PL" || role === "PM" || role === "PO";

  if (loading && !data) {
    return (
      <div className="p-6 space-y-4 animate-pulse">
        <div className="h-5 bg-[#e5e7eb] rounded w-60" />
        <div className="h-56 bg-[#e5e7eb] rounded-lg" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* ── Top Tabs ── */}
      <div className="flex items-center gap-1 bg-[#f3f4f6] rounded-lg p-1 w-fit mb-5">
        <button className={tabCls("categorisation")} onClick={() => setActiveTab("categorisation")}>
          <HiOutlineChartPie className="inline mr-1 text-sm" />Categorisation
        </button>
        <button className={tabCls("fi")} onClick={() => setActiveTab("fi")}>
          <HiOutlineDocumentReport className="inline mr-1 text-sm" />PO Compliance
        </button>
        <button className={tabCls("competency")} onClick={() => setActiveTab("competency")}>
          <HiOutlineAcademicCap className="inline mr-1 text-sm" />Competency Stats
        </button>
      </div>

      {/* ═══ TAB: Categorisation ═══ */}
      {activeTab === "categorisation" && (
        <div>
          <FilterPanel
            fState={filters} onChange={handleFilterChange} opts={catOpts}
            showGrade onClear={() => setFilters({ cycle_id: "", state_id: (role === "SPL" || isPmOrPo) ? filters.state_id : "", district_id: "", block_id: "", school_id: "", grade: "", cycle_type: "" })}
            hasClear={hasAnyFilter(filters)}
            role={role} isPmOrPo={isPmOrPo} splStates={splStates} pmBlocks={pmBlocks} plDistricts={plDistricts} adminStates={adminStates} pmDistrictOpts={pmDistrictOpts}
          />

          {!filters.state_id ? (
            <div className="bg-white rounded-xl border border-[#e5e7eb] p-10 text-center">
              <HiOutlineChartPie className="mx-auto text-3xl text-[#d0d5dd] mb-2" />
              <p className="text-[13px] text-[#57606a]">Select a state to view categorisation data.</p>
            </div>
          ) : !filters.cycle_id ? (
            <div className="bg-white rounded-xl border border-[#e5e7eb] p-10 text-center">
              <HiOutlineChartPie className="mx-auto text-3xl text-[#d0d5dd] mb-2" />
              <p className="text-[13px] text-[#57606a]">Select a cycle to see categorisation data.</p>
            </div>
          ) : (
            <>
              {/* ── Student Categorisation — All Categories as Cards ── */}
              <div className="mb-5">
                <h3 className="text-[11px] font-semibold text-[#8b949e] uppercase tracking-wider mb-3 px-1">Student Categorisation</h3>
                {(() => {
                  const namedCategories = categories.filter(c => c.name !== "Uncategorised");
                  if (categories.length === 0) {
                    return (
                      <div className="bg-white rounded-xl border border-[#e5e7eb] p-10 text-center">
                        <HiOutlineChartPie className="mx-auto text-3xl text-[#d0d5dd] mb-2" />
                        <p className="text-[12px] text-[#57606a]">No student scores found.</p>
                      </div>
                    );
                  }
                  if (namedCategories.length === 0) {
                    // Scores exist, but no student category thresholds matched — every
                    // student landed in "Uncategorised". Say so instead of a blank grid.
                    return (
                      <div className="bg-white rounded-xl border border-[#e5e7eb] p-10 text-center">
                        <HiOutlineChartPie className="mx-auto text-3xl text-[#d0d5dd] mb-2" />
                        <p className="text-[12px] text-[#57606a]">
                          All {data?.total_students || 0} student{(data?.total_students || 0) === 1 ? "" : "s"} are Uncategorised — no student category thresholds matched (or none are configured for this cycle).
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {namedCategories.map((cat) => (
                        <CategoryCard key={cat.name} cat={cat} type="student" total={data?.total_students || 0} />
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* ── School Categorisation — All Categories as Cards ── */}
              <div>
                <h3 className="text-[11px] font-semibold text-[#8b949e] uppercase tracking-wider mb-3 px-1">School Categorisation</h3>
                {(() => {
                  const namedSchoolCategories = schoolCategories.filter(c => c.name !== "Uncategorised");
                  if (schoolCategories.length === 0 || (data?.total_schools || 0) === 0) {
                    return (
                      <div className="bg-white rounded-xl border border-[#e5e7eb] p-10 text-center">
                        <HiOutlineOfficeBuilding className="mx-auto text-3xl text-[#d0d5dd] mb-2" />
                        <p className="text-[12px] text-[#57606a]">No school data found.</p>
                      </div>
                    );
                  }
                  if (namedSchoolCategories.length === 0) {
                    return (
                      <div className="bg-white rounded-xl border border-[#e5e7eb] p-10 text-center">
                        <HiOutlineOfficeBuilding className="mx-auto text-3xl text-[#d0d5dd] mb-2" />
                        <p className="text-[12px] text-[#57606a]">
                          All {data?.total_schools || 0} school{(data?.total_schools || 0) === 1 ? "" : "s"} are Uncategorised — no school category thresholds matched (or none are configured for this cycle).
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {namedSchoolCategories.map((cat) => (
                        <CategoryCard key={cat.name} cat={cat} type="school" total={data?.total_schools || 0} />
                      ))}
                    </div>
                  );
                })()}
              </div>
            </>
          )}
        </div>
      )}

      {/* ═══ TAB: PO Compliance ═══ */}
      {activeTab === "fi" && (
        <div>
          <FilterPanel
            fState={fiFilters} onChange={handleFiFilterChange} opts={fiOpts}
            onClear={() => { setFiPage(1); setFiFilters({ cycle_id: "", state_id: (role === "SPL" || isPmOrPo) ? fiFilters.state_id : "", district_id: "", block_id: "", school_id: "", cycle_type: "" }); }}
            hasClear={hasAnyFilter(fiFilters)}
            role={role} isPmOrPo={isPmOrPo} splStates={splStates} pmBlocks={pmBlocks} plDistricts={plDistricts} adminStates={adminStates} pmDistrictOpts={pmDistrictOpts}
          >
            <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">Export Cycles</label>
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <ChipMultiSelect
                  options={fiOpts.cycles}
                  selectedIds={exportCycleIds}
                  onAdd={(id) => setExportCycleIds((prev) => [...prev, id])}
                  onRemove={(id) => setExportCycleIds((prev) => prev.filter((x) => x !== id))}
                  placeholder="Select cycles to export…"
                  emptyMessage="No cycles available"
                  getLabel={cycleName}
                />
              </div>
              <button
                onClick={handleExportAssessmentData}
                disabled={exportCycleIds.length === 0 || exportLoading}
                className="shrink-0 inline-flex items-center gap-1.5 h-[42px] px-3 text-[11.5px] font-medium rounded-lg cursor-pointer bg-[#0078d4] text-white border-none hover:bg-[#106ebe] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exportLoading ? "Exporting..." : "Export"}
              </button>
            </div>
          </FilterPanel>
          {!fiFilters.state_id ? (
            <div className="bg-white rounded-xl border border-[#e5e7eb] p-10 text-center">
              <HiOutlineDocumentReport className="mx-auto text-3xl text-[#d0d5dd] mb-2" />
              <p className="text-[13px] text-[#57606a]">Select a state to view PO compliance data.</p>
            </div>
          ) : !fiFilters.cycle_id ? (
            <div className="bg-white rounded-xl border border-[#e5e7eb] p-10 text-center">
              <HiOutlineDocumentReport className="mx-auto text-3xl text-[#d0d5dd] mb-2" />
              <p className="text-[13px] text-[#57606a]">Select a cycle to see PO compliance data.</p>
            </div>
          ) : (
          <>
          {fiData?.summary && (
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-5">
              {[
                { label: "Total Visits", value: fiData.summary.total_visits, icon: HiOutlineClipboardCheck, color: "#3b82f6", bg: "#eff6ff" },
                { label: "Completed", value: fiData.summary.completed, icon: HiOutlineCheckCircle, color: "#16a34a", bg: "#f0fdf4" },
                { label: "Scheduled", value: fiData.summary.scheduled, icon: HiOutlineClock, color: "#d97706", bg: "#fffbeb" },
                { label: "Missed", value: fiData.summary.missed, icon: HiOutlineExclamationCircle, color: "#dc2626", bg: "#fef2f2" },
                { label: "Schools", value: fiData.summary.unique_schools, icon: HiOutlineAcademicCap, color: "#7c3aed", bg: "#f5f3ff" },
                { label: "Completion Rate", value: `${fiData.summary.completion_rate}%`, icon: HiOutlineChartPie, color: "#0891b2", bg: "#ecfeff" },
              ].map((kpi) => (
                <div key={kpi.label} className="rounded-xl border border-[#e5e7eb] px-4 py-3" style={{ backgroundColor: kpi.bg }}>
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shadow-sm" style={{ backgroundColor: kpi.color }}>
                      <kpi.icon className="text-sm text-white" />
                    </div>
                    <div>
                      <div className="text-[9px] text-[#8b949e] font-medium uppercase">{kpi.label}</div>
                      <div className="text-[15px] font-bold text-[#24292f]">{kpi.value}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {fiLoading ? (
            <div className="bg-white rounded-xl border border-[#e5e7eb] p-8 text-center"><p className="text-sm text-[#57606a]">Loading...</p></div>
          ) : fiData?.visits?.length > 0 ? (
            <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden">
              <div className="px-4 py-2.5 border-b border-[#e5e7eb] bg-[#f8f9fa]">
                <h3 className="text-[11px] font-semibold text-[#8b949e] uppercase tracking-wider">Visit Details ({fiData.pagination?.total || fiData.visits.length})</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[800px]">
                  <thead><tr className="bg-[#f6f8fa] border-b border-[#e5e7eb]">
                    {["Date","School","UDISE","Block","District","State","Cycle","Round","Status","PO Name"].map(h=>(
                      <th key={h} className="text-left px-3 py-2.5 text-[10.5px] font-medium text-[#8b949e] uppercase tracking-wide">{h}</th>
                    ))}
                  </tr></thead>
                  <tbody className="divide-y divide-[#e5e7eb]">
                    {fiData.visits.map((v)=>(
                      <tr key={v.visit_id} className="hover:bg-[#f6f8fa] transition-colors">
                        <td className="px-3 py-2 text-[12.5px] text-[#24292f] whitespace-nowrap">{v.visit_date}</td>
                        <td className="px-3 py-2 text-[12.5px] text-[#24292f] font-medium">{v.school_name||"-"}</td>
                        <td className="px-3 py-2 text-[11px] text-[#57606a] font-mono">{v.udise_code||"-"}</td>
                        <td className="px-3 py-2 text-[12.5px] text-[#57606a]">{v.block_name||"-"}</td>
                        <td className="px-3 py-2 text-[12.5px] text-[#57606a]">{v.district_name||"-"}</td>
                        <td className="px-3 py-2 text-[12.5px] text-[#57606a]">{v.state_name||"-"}</td>
                        <td className="px-3 py-2 text-[12.5px] text-[#57606a]">{v.cycle_title||"-"}</td>
                        <td className="px-3 py-2 text-[12.5px] text-[#57606a]">{v.round_number||"-"}</td>
                        <td className="px-3 py-2"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${v.status==="COMPLETED"?"bg-green-100 text-green-700":v.status==="MISSED"?"bg-red-100 text-red-700":"bg-yellow-100 text-yellow-700"}`}>{v.status}</span></td>
                        <td className="px-3 py-2 text-[12.5px] text-[#57606a] truncate max-w-[120px]">{v.fi_user_name||v.fi_user_id||"-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {fiData.pagination?.total > 0 && (
                <Pagination
                  currentPage={fiData.pagination.page}
                  totalPages={fiData.pagination.total_pages}
                  totalCount={fiData.pagination.total}
                  pageSize={fiData.pagination.page_size}
                  onPageChange={(p) => setFiPage(p)}
                  entityName="visits"
                />
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-[#e5e7eb] p-10 text-center">
              <HiOutlineDocumentReport className="mx-auto text-3xl text-[#d0d5dd] mb-2" />
              <p className="text-[13px] text-[#57606a]">No visits found.</p>
            </div>
          )}
          </>
          )}
        </div>
      )}

      {/* ═══ TAB: Competency ═══ */}
      {activeTab === "competency" && (
        <div>
          <div className="bg-white rounded-xl border border-[#e5e7eb] mb-5">
            <div className="px-5 py-3 border-b border-[#f0f0f0]"><span className="text-[11px] font-semibold text-[#8b949e] uppercase tracking-wider">Filters</span></div>
            <div className="px-5 py-4 flex flex-wrap items-end gap-3">
              <div className="min-w-[140px]">
                <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">State</label>
                {role === "PM" ? (
                  <select value={compFilters.state_id || ""} onChange={e => setCompFilters({...compFilters, state_id: e.target.value, cycle_type: "", cycle_id: "", district_id: "", block_id: ""})} className={selectCls + " w-full"}>
                    {[...new Map(pmBlocks.map(b => [b.state_id, { id: b.state_id, name: b.state_name }])).values()]
                      .map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                ) : role === "SPL" ? (
                  <select value={compFilters.state_id || ""} onChange={e => setCompFilters({...compFilters, state_id: e.target.value, cycle_type: "", cycle_id: "", district_id: "", block_id: ""})} className={selectCls + " w-full"}>
                    {splStates.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                ) : role === "PL" ? (
                  <select value={compFilters.state_id || ""} onChange={e => setCompFilters({...compFilters, state_id: e.target.value, cycle_type: "", cycle_id: "", district_id: "", block_id: ""})} className={selectCls + " w-full"}>
                    {[...new Map(plDistricts.map(d => [d.state_id, { id: d.state_id, name: d.state_name }])).values()]
                      .map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                ) : isPmOrPo ? (
                  <div className={selectCls + " w-full bg-[#f6f8fa] text-[#57606a] flex items-center"}>
                    {adminStates.find(s => String(s.id) === String(compFilters.state_id))?.name || (compFilters.state_id ? `State #${compFilters.state_id}` : "—")}
                  </div>
                ) : (
                  <SearchableDropdown
                    value={String(compFilters.state_id || "")}
                    onChange={(id) => setCompFilters({...compFilters, state_id: id, cycle_type: "", cycle_id: "", district_id: "", block_id: ""})}
                    placeholder="Select State"
                    fetchFn={(search, page = 1) => fetchMasterData("states", { page, page_size: 20, search, has_assessment: true })}
                    variant="report"
                  />
                )}
              </div>
              <div className="min-w-[130px]">
                <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">Cycle Type</label>
                <select value={compFilters.cycle_type||""} onChange={e=>{setCompFilters({...compFilters,cycle_type:e.target.value,cycle_id:""});}} className={selectCls+" w-full"}>
                  <option value="">Select a Cycle Type</option>
                  <option value="Continuous">Continuous</option>
                  <option value="Quarterly">Quarterly</option>
                </select>
              </div>
              <div className="min-w-[160px]">
                <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">Cycle</label>
                <select value={compFilters.cycle_id} onChange={e=>setCompFilters({...compFilters,cycle_id:e.target.value})} className={selectCls+" w-full"}>
                  <option value="">Select a Cycle</option>
                  {compCycles.map(c=><option key={c.id} value={c.id}>{cycleName(c)}</option>)}
                </select>
              </div>
              <div className="min-w-[140px]">
                <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">District</label>
                {role === "PM" ? (
                  <select value={compFilters.district_id||""} onChange={e=>setCompFilters({...compFilters,district_id:e.target.value,block_id:""})} className={selectCls+" w-full"} disabled={!compFilters.state_id}>
                    <option value="">All Districts</option>
                    {pmDistrictOpts.slice().sort((a,b)=>a.name.localeCompare(b.name)).map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                ) : role === "PL" ? (
                  <select value={compFilters.district_id||""} onChange={e=>setCompFilters({...compFilters,district_id:e.target.value,block_id:""})} className={selectCls+" w-full"} disabled={!compFilters.state_id}>
                    <option value="">All Districts</option>
                    {plDistricts.slice().sort((a,b)=>a.name.localeCompare(b.name)).map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                ) : (
                  <SearchableDropdown
                    key={`comp-dist-${compFilters.state_id}`}
                    value={String(compFilters.district_id||"")}
                    onChange={(id) => setCompFilters({...compFilters, district_id: id, block_id: ""})}
                    placeholder="All Districts"
                    fetchFn={(search, page = 1) => fetchMasterData("districts", {
                      page, page_size: 20, state_id: Number(compFilters.state_id), search,
                      has_assessment: true,
                      ...(compFilters.cycle_id ? { cycle_id: compFilters.cycle_id } : {}),
                      ...(compFilters.cycle_type ? { cycle_type: compFilters.cycle_type.toLowerCase() } : {}),
                    })}
                    disabled={!compFilters.state_id}
                    variant="report"
                  />
                )}
              </div>
              <div className="min-w-[140px]">
                <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">Block</label>
                {role === "PM" ? (
                  <select value={compFilters.block_id||""} onChange={e=>setCompFilters({...compFilters,block_id:e.target.value})} className={selectCls+" w-full"} disabled={!compFilters.state_id}>
                    <option value="">All Blocks</option>
                    {pmBlocks.filter(b => !compFilters.district_id || String(b.district_id) === String(compFilters.district_id)).slice().sort((a,b)=>a.name.localeCompare(b.name)).map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                ) : (
                  <SearchableDropdown
                    key={`comp-blk-${compFilters.district_id}-${compFilters.state_id}`}
                    value={String(compFilters.block_id||"")}
                    onChange={(id) => setCompFilters({...compFilters, block_id: id})}
                    placeholder="All Blocks"
                    fetchFn={(search, page = 1) => fetchMasterData("blocks", {
                      page, page_size: 20,
                      ...(compFilters.district_id ? { district_id: Number(compFilters.district_id) } : { state_id: Number(compFilters.state_id) }),
                      search,
                      has_assessment: true,
                      ...(compFilters.cycle_id ? { cycle_id: compFilters.cycle_id } : {}),
                      ...(compFilters.cycle_type ? { cycle_type: compFilters.cycle_type.toLowerCase() } : {}),
                    })}
                    disabled={!compFilters.state_id}
                    variant="report"
                  />
                )}
              </div>
              <div className="min-w-[110px]">
                <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">Grade</label>
                <select value={compFilters.grade} onChange={e=>setCompFilters({...compFilters,grade:e.target.value})} className={selectCls+" w-full"}>
                  <option value="">All Grades</option>
                  {[1,2,3,4,5,6,7,8].map(g=><option key={g} value={g}>Grade {g}</option>)}
                </select>
              </div>
              {hasAnyFilter(compFilters) && (
                <button onClick={()=>setCompFilters({cycle_id:"",grade:"",state_id: (role === "SPL" || isPmOrPo) ? compFilters.state_id : "",cycle_type:"",district_id:"",block_id:""})} className="inline-flex items-center gap-1 h-[34px] px-3 text-[12px] font-medium rounded-lg cursor-pointer bg-[#f3f4f6] text-[#57606a] border border-[#d0d5dd] hover:bg-[#e5e7eb] transition-all">
                  <HiOutlineX className="text-xs" /> Clear
                </button>
              )}
            </div>
          </div>
          {!compFilters.state_id ? (
            <div className="bg-white rounded-xl border border-[#e5e7eb] p-10 text-center">
              <HiOutlineAcademicCap className="mx-auto text-3xl text-[#d0d5dd] mb-2" />
              <p className="text-[13px] text-[#57606a]">Select a state to view competency data.</p>
            </div>
          ) : !compFilters.cycle_id ? (
            <div className="bg-white rounded-xl border border-[#e5e7eb] p-10 text-center">
              <HiOutlineAcademicCap className="mx-auto text-3xl text-[#d0d5dd] mb-2" />
              <p className="text-[13px] text-[#57606a]">Select a cycle to see competency data.</p>
            </div>
          ) : compLoading ? (
            <div className="bg-white rounded-xl border border-[#e5e7eb] p-8 text-center"><p className="text-sm text-[#57606a]">Loading...</p></div>
          ) : compData?.competencies?.length > 0 ? (
            <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden">
              <div className="px-4 py-2.5 border-b border-[#e5e7eb] bg-[#f8f9fa]">
                <h3 className="text-[11px] font-semibold text-[#8b949e] uppercase tracking-wider">Competency Achievement ({compData.competencies.length})</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px]">
                  <thead><tr className="bg-[#f6f8fa] border-b border-[#e5e7eb]">
                    {["Competency Name","Competency Code","Weight","Total Students","Tested","Mastered","Mastery %"].map(h=>(
                      <th key={h} className="text-left px-3 py-2.5 text-[10.5px] font-medium text-[#8b949e] uppercase tracking-wide">{h}</th>
                    ))}
                  </tr></thead>
                  <tbody className="divide-y divide-[#e5e7eb]">
                    {compData.competencies.map((c)=>(
                      <tr key={c.competency_code} className="hover:bg-[#f6f8fa] transition-colors">
                        <td className="px-3 py-2 text-[12.5px] font-medium text-[#24292f]">{c.competency_name || c.competency_code}</td>
                        <td className="px-3 py-2 text-[12.5px] text-[#57606a]">{c.competency_code}</td>
                        <td className="px-3 py-2 text-[12.5px] text-[#57606a]">{c.weight}</td>
                        <td className="px-3 py-2 text-[12.5px] text-[#24292f] font-semibold">{c.total_eligible_students}</td>
                        <td className="px-3 py-2 text-[12.5px] text-[#24292f] font-semibold">{c.total_students}</td>
                        <td className="px-3 py-2 text-[12.5px] text-[#16a34a] font-semibold">{c.mastered_count}</td>
                        <td className="px-3 py-2"><div className="flex items-center gap-1.5"><div className="w-14 bg-[#f0f0f0] rounded-full h-1.5"><div className="h-1.5 rounded-full bg-[#0078d4]" style={{width:`${c.mastery_rate}%`}} /></div><span className="text-[11px] text-[#57606a]">{c.mastery_rate}%</span></div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-[#e5e7eb] p-10 text-center">
              <HiOutlineAcademicCap className="mx-auto text-3xl text-[#d0d5dd] mb-2" />
              <p className="text-[13px] text-[#57606a]">No competency data available.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Dashboard;
